import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Trash2, Plus, ArrowUp, ArrowDown } from "lucide-react";

export const Route = createFileRoute("/admin/nav")({
  component: AdminNavPage,
});

type NavLink = {
  id: string;
  label: string;
  path: string;
  search_key: string | null;
  search_value: string | null;
  position: number;
  enabled: boolean;
};

function AdminNavPage() {
  const [links, setLinks] = useState<NavLink[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const { data, error } = await supabase
      .from("nav_links")
      .select("*")
      .order("position", { ascending: true });
    if (error) toast.error(error.message);
    else setLinks((data as NavLink[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const addLink = async () => {
    const nextPos = (links[links.length - 1]?.position ?? 0) + 1;
    const { error } = await supabase.from("nav_links").insert({
      label: "New Link",
      path: "/shop",
      position: nextPos,
      enabled: true,
    });
    if (error) toast.error(error.message);
    else { toast.success("Added"); load(); }
  };

  const updateLink = async (id: string, patch: Partial<NavLink>) => {
    setLinks((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
    const { error } = await supabase.from("nav_links").update(patch).eq("id", id);
    if (error) toast.error(error.message);
  };

  const removeLink = async (id: string) => {
    if (!confirm("Remove this link?")) return;
    const { error } = await supabase.from("nav_links").delete().eq("id", id);
    if (error) toast.error(error.message);
    else { toast.success("Removed"); load(); }
  };

  const move = async (id: string, dir: -1 | 1) => {
    const idx = links.findIndex((l) => l.id === id);
    const swap = links[idx + dir];
    if (!swap) return;
    const a = links[idx];
    await Promise.all([
      supabase.from("nav_links").update({ position: swap.position }).eq("id", a.id),
      supabase.from("nav_links").update({ position: a.position }).eq("id", swap.id),
    ]);
    load();
  };

  if (loading) return <p className="text-sm text-muted-foreground">Loading...</p>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold">Header Navigation</h2>
          <p className="text-sm text-muted-foreground">Customize the links shown in the top menu.</p>
        </div>
        <button
          onClick={addLink}
          className="inline-flex items-center gap-2 bg-foreground text-background px-4 py-2 text-sm rounded-md hover:opacity-90"
        >
          <Plus className="h-4 w-4" /> Add Link
        </button>
      </div>

      <div className="space-y-3">
        {links.map((link, i) => (
          <div key={link.id} className="bg-card border border-border rounded-md p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
            <div className="md:col-span-3">
              <label className="text-xs text-muted-foreground">Label</label>
              <input
                value={link.label}
                onChange={(e) => setLinks((p) => p.map((l) => l.id === link.id ? { ...l, label: e.target.value } : l))}
                onBlur={(e) => updateLink(link.id, { label: e.target.value })}
                className="mt-1 w-full border border-border rounded px-2 py-1.5 text-sm bg-background"
              />
            </div>
            <div className="md:col-span-3">
              <label className="text-xs text-muted-foreground">Path</label>
              <input
                value={link.path}
                onChange={(e) => setLinks((p) => p.map((l) => l.id === link.id ? { ...l, path: e.target.value } : l))}
                onBlur={(e) => updateLink(link.id, { path: e.target.value })}
                placeholder="/shop"
                className="mt-1 w-full border border-border rounded px-2 py-1.5 text-sm bg-background"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-muted-foreground">Filter Key</label>
              <input
                value={link.search_key ?? ""}
                onChange={(e) => setLinks((p) => p.map((l) => l.id === link.id ? { ...l, search_key: e.target.value } : l))}
                onBlur={(e) => updateLink(link.id, { search_key: e.target.value || null })}
                placeholder="category"
                className="mt-1 w-full border border-border rounded px-2 py-1.5 text-sm bg-background"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-muted-foreground">Filter Value</label>
              <input
                value={link.search_value ?? ""}
                onChange={(e) => setLinks((p) => p.map((l) => l.id === link.id ? { ...l, search_value: e.target.value } : l))}
                onBlur={(e) => updateLink(link.id, { search_value: e.target.value || null })}
                placeholder="tops"
                className="mt-1 w-full border border-border rounded px-2 py-1.5 text-sm bg-background"
              />
            </div>
            <div className="md:col-span-2 flex items-end justify-end gap-1">
              <label className="flex items-center gap-1 text-xs mr-2">
                <input
                  type="checkbox"
                  checked={link.enabled}
                  onChange={(e) => updateLink(link.id, { enabled: e.target.checked })}
                />
                On
              </label>
              <button onClick={() => move(link.id, -1)} disabled={i === 0} className="p-1.5 border border-border rounded hover:bg-muted disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5"/></button>
              <button onClick={() => move(link.id, 1)} disabled={i === links.length - 1} className="p-1.5 border border-border rounded hover:bg-muted disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5"/></button>
              <button onClick={() => removeLink(link.id)} className="p-1.5 border border-border rounded hover:bg-destructive hover:text-destructive-foreground"><Trash2 className="h-3.5 w-3.5"/></button>
            </div>
          </div>
        ))}
        {links.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-10">No nav links yet. Click "Add Link" to create one.</p>
        )}
      </div>
    </div>
  );
}
